/**
 * Resource Grouping Utilities
 * Functions for organizing and filtering resources in horizontal calendar views
 */

import type { Resource, CalendarEvent } from '@/api/types/calendars.types';

/**
 * Resource type priority for sorting
 * Lower number = higher priority
 */
const RESOURCE_TYPE_PRIORITY: Record<string, number> = {
  AIRPLANE: 1,
  INSTRUCTOR: 2,
  PILOT: 3,
  AIRPORT: 4,
};

/**
 * Grouped resources by type
 */
export interface GroupedResources {
  airplanes: Resource[];
  instructors: Resource[];
  pilots: Resource[];
  airports: Resource[];
}

/**
 * Group resources by type and sort them
 * Priority order: AIRPLANE → INSTRUCTOR → PILOT → AIRPORT
 * Within each type, sort alphabetically by name
 *
 * @param resources - Array of resources to group
 * @param selectedIds - Optional array of selected resource IDs to filter by
 * @returns Grouped resources by type
 */
export const groupResourcesByType = (
  resources: Resource[],
  selectedIds?: string[]
): GroupedResources => {
  // Filter by selected IDs if provided
  const filteredResources =
    selectedIds && selectedIds.length > 0
      ? resources.filter((r) => selectedIds.includes(r.id))
      : resources;

  // Group by resource type
  const grouped: GroupedResources = {
    airplanes: [],
    instructors: [],
    pilots: [],
    airports: [],
  };

  filteredResources.forEach((resource) => {
    switch (resource.resource_type) {
      case 'AIRPLANE':
        grouped.airplanes.push(resource);
        break;
      case 'INSTRUCTOR':
        grouped.instructors.push(resource);
        break;
      case 'PILOT':
        grouped.pilots.push(resource);
        break;
      case 'AIRPORT':
        grouped.airports.push(resource);
        break;
    }
  });

  // Sort each group alphabetically by name
  Object.values(grouped).forEach((group: Resource[]) => {
    group.sort((a, b) => a.name.localeCompare(b.name));
  });

  return grouped;
};

/**
 * Get resources that have events in the given period
 * Filters resources to only those with at least one event
 * Sorted by type priority, then alphabetically
 *
 * @param events - Array of calendar events
 * @param allResources - Array of all available resources
 * @param selectedIds - Optional array of selected resource IDs to filter by
 * @returns Sorted array of resources that have events
 */
export const getActiveResources = (
  events: CalendarEvent[],
  allResources: Resource[],
  selectedIds?: string[]
): Resource[] => {
  // Extract unique resource IDs from events
  const resourceIdsInEvents = new Set<string>();
  events.forEach((event) => {
    event.resources.forEach((resource) => {
      resourceIdsInEvents.add(resource.resource_id);
    });
  });

  // Filter resources that:
  // 1. Have at least one event
  // 2. Match selection filter (if any)
  const activeResources = allResources.filter((resource) => {
    const hasEvents = resourceIdsInEvents.has(resource.id);
    const matchesFilter =
      !selectedIds ||
      selectedIds.length === 0 ||
      selectedIds.includes(resource.id);
    return hasEvents && matchesFilter;
  });

  // Sort by resource type priority, then by name
  activeResources.sort((a, b) => {
    const priorityA = RESOURCE_TYPE_PRIORITY[a.resource_type] || 99;
    const priorityB = RESOURCE_TYPE_PRIORITY[b.resource_type] || 99;

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return a.name.localeCompare(b.name);
  });

  return activeResources;
};

/**
 * Filter events that belong to a specific resource
 *
 * @param events - Array of calendar events
 * @param resourceId - ID of the resource to filter by
 * @returns Array of events that include this resource
 */
export const getEventsForResource = (
  events: CalendarEvent[],
  resourceId: string
): CalendarEvent[] => {
  return events.filter((event) =>
    event.resources.some((resource) => resource.resource_id === resourceId)
  );
};

/**
 * Get all unique resource IDs from events
 *
 * @param events - Array of calendar events
 * @returns Set of unique resource IDs
 */
export const getResourceIdsFromEvents = (
  events: CalendarEvent[]
): Set<string> => {
  const resourceIds = new Set<string>();
  events.forEach((event) => {
    event.resources.forEach((resource) => {
      resourceIds.add(resource.resource_id);
    });
  });
  return resourceIds;
};

/**
 * Get resources by type
 *
 * @param resources - Array of resources
 * @param resourceType - Type of resource to filter by
 * @returns Array of resources of the specified type
 */
export const getResourcesByType = (
  resources: Resource[],
  resourceType: 'AIRPLANE' | 'INSTRUCTOR' | 'PILOT' | 'AIRPORT'
): Resource[] => {
  return resources
    .filter((r) => r.resource_type === resourceType)
    .sort((a, b) => a.name.localeCompare(b.name));
};
